/**
 * 상태를 화면에 반영한다. 여기서 Serial 도 Python 도 부르지 않는다.
 * 갱신은 최대 10Hz — 바이트마다 DOM 을 건드리지 않는다.
 */
import type { InputController } from "../input/controller";
import type { WebCarEngine } from "../engine/web-car-engine";
import type { EngineView, ProductInfo, SensorValue } from "../types";

const REFRESH_MS = 100;

function need<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`화면 요소를 찾지 못했습니다: #${id}`);
  return element as T;
}

/** 아직 받지 못한 값은 0 이 아니라 — 로 적는다. */
function dash(value: number | null | undefined, suffix = ""): string {
  return value === null || value === undefined ? "—" : `${value}${suffix}`;
}

function formatSensor(value: SensorValue): string {
  return Array.isArray(value) ? `[${value.join(", ")}]` : String(value);
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
}

function percentile(values: number[], ratio: number): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.floor(sorted.length * ratio));
  return sorted[index]!;
}

export interface UiPorts {
  /** 이미 허용한 포트 목록 — 사용자가 직접 골라야 연결된다. */
  refresh(): Promise<void>;
}

export interface RenderOptions {
  engine: WebCarEngine;
  controller: InputController;
  onRequestPort: (filtered: boolean) => void;
  onConnectKnown: (index: number) => void;
  onError: (error: unknown) => void;
}

export class Ui {
  private readonly engine: WebCarEngine;
  private readonly controller: InputController;
  private readonly options: RenderOptions;
  private outputsBuiltFor: string | null = null;
  private pending: EngineView | null = null;
  private timer: number | null = null;
  private lastEventCount = 0;

  private readonly el = {
    boot: need<HTMLParagraphElement>("boot"),
    panels: {
      connection: need<HTMLElement>("panel-connection"),
      drive: need<HTMLElement>("panel-drive"),
      outputs: need<HTMLElement>("panel-outputs"),
      sensors: need<HTMLElement>("panel-sensors"),
      diagnostics: need<HTMLElement>("panel-diagnostics"),
    },
    product: need<HTMLSelectElement>("product"),
    leftSign: need<HTMLSelectElement>("left-sign"),
    rightSign: need<HTMLSelectElement>("right-sign"),
    requestPort: need<HTMLButtonElement>("request-port"),
    requestAnyPort: need<HTMLButtonElement>("request-any-port"),
    knownPorts: need<HTMLSelectElement>("known-ports"),
    connectKnown: need<HTMLButtonElement>("connect-known"),
    disconnect: need<HTMLButtonElement>("disconnect"),
    stateLine: need<HTMLParagraphElement>("state-line"),
    hintLine: need<HTMLParagraphElement>("hint-line"),
    emergencyStop: need<HTMLButtonElement>("emergency-stop"),
    activate: need<HTMLButtonElement>("activate"),
    activateReason: need<HTMLSpanElement>("activate-reason"),
    speedReadout: need<HTMLParagraphElement>("speed-readout"),
    maxSpeed: need<HTMLInputElement>("max-speed"),
    maxSpeedValue: need<HTMLSpanElement>("max-speed-value"),
    outputs: need<HTMLDivElement>("outputs"),
    sensors: need<HTMLTableElement>("sensors"),
    diagnostics: need<HTMLTableElement>("diagnostics"),
    events: need<HTMLUListElement>("events"),
  };

  constructor(options: RenderOptions) {
    this.engine = options.engine;
    this.controller = options.controller;
    this.options = options;
    this.buildProducts();
    this.wire();
  }

  /** 런타임이 준비되기 전의 안내. */
  setBootMessage(message: string): void {
    this.el.boot.textContent = message;
    this.el.boot.hidden = false;
  }

  showApp(): void {
    this.el.boot.hidden = true;
    for (const panel of Object.values(this.el.panels)) panel.hidden = false;
  }

  setKnownPorts(labels: string[]): void {
    this.el.knownPorts.replaceChildren(
      ...labels.map((label, index) => new Option(label, String(index))),
    );
    this.el.connectKnown.disabled = labels.length === 0;
  }

  /** 10Hz 로 묶어서 그린다. */
  schedule(view: EngineView): void {
    this.pending = view;
    if (this.timer !== null) return;
    this.timer = window.setTimeout(() => {
      this.timer = null;
      const next = this.pending;
      this.pending = null;
      if (next) this.draw(next);
    }, REFRESH_MS);
  }

  drawNow(view: EngineView): void {
    this.pending = null;
    this.draw(view);
  }

  private buildProducts(): void {
    this.el.product.replaceChildren(
      ...this.engine.catalog.map((item) => new Option(`${item.label} [${item.id}]`, item.id)),
    );
    this.el.product.value = this.engine.settings.productId;
    this.applySettingsToControls();
  }

  private applySettingsToControls(): void {
    const settings = this.engine.settings;
    this.el.maxSpeed.value = String(settings.maxSpeed);
    this.el.maxSpeedValue.textContent = `${settings.maxSpeed}%`;
    this.el.leftSign.value = String(settings.leftSign);
    this.el.rightSign.value = String(settings.rightSign);
  }

  private wire(): void {
    const guard = (run: () => void) => {
      try {
        run();
      } catch (error) {
        this.options.onError(error);
      }
    };

    this.el.product.addEventListener("change", () =>
      guard(() => {
        this.engine.selectProduct(this.el.product.value);
        this.applySettingsToControls();
        this.outputsBuiltFor = null;
      }),
    );
    const signs = () =>
      guard(() =>
        this.engine.setSigns(Number(this.el.leftSign.value), Number(this.el.rightSign.value)),
      );
    this.el.leftSign.addEventListener("change", signs);
    this.el.rightSign.addEventListener("change", signs);

    this.el.requestPort.addEventListener("click", () => this.options.onRequestPort(true));
    this.el.requestAnyPort.addEventListener("click", () => this.options.onRequestPort(false));
    this.el.connectKnown.addEventListener("click", () =>
      this.options.onConnectKnown(Number(this.el.knownPorts.value)),
    );
    this.el.disconnect.addEventListener("click", () => {
      void this.engine.disconnect().catch(this.options.onError);
    });

    this.el.emergencyStop.addEventListener("click", () => {
      void this.engine.emergencyStop("비상정지 버튼").catch(this.options.onError);
    });
    this.el.activate.addEventListener("click", () => guard(() => this.engine.activate()));

    this.el.maxSpeed.addEventListener("input", () =>
      guard(() => {
        const value = this.engine.setMaxSpeed(Number(this.el.maxSpeed.value));
        this.el.maxSpeedValue.textContent = `${value}%`;
      }),
    );

    for (const button of document.querySelectorAll<HTMLButtonElement>("button.dir")) {
      const direction = button.dataset.direction as "forward" | "back" | "left" | "right";
      button.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        button.setPointerCapture(event.pointerId);
        button.setAttribute("aria-pressed", "true");
        this.controller.pointerDown(event.pointerId, direction);
      });
      const release = (event: PointerEvent) => {
        button.removeAttribute("aria-pressed");
        this.controller.pointerUp(event.pointerId);
      };
      button.addEventListener("pointerup", release);
      button.addEventListener("pointerleave", release);
      // 취소와 캡처 상실은 그 입력만이 아니라 전부 정지시킨다.
      button.addEventListener("pointercancel", () => {
        button.removeAttribute("aria-pressed");
        this.controller.pointerCancel();
      });
      button.addEventListener("lostpointercapture", () => {
        button.removeAttribute("aria-pressed");
        this.controller.pointerCancel();
      });
    }
  }

  private buildOutputs(product: ProductInfo): void {
    if (this.outputsBuiltFor === product.id) return;
    this.outputsBuiltFor = product.id;
    this.el.outputs.replaceChildren(
      ...product.outputs.map((spec) => {
        const row = document.createElement("div");
        row.className = "out-row";

        const label = document.createElement("label");
        label.textContent = spec.label;
        label.htmlFor = `out-${spec.port}`;

        const slider = document.createElement("input");
        slider.type = "range";
        slider.id = `out-${spec.port}`;
        slider.min = String(spec.min);
        slider.max = String(spec.max);
        slider.step = "1";
        slider.value = "0";
        slider.dataset.port = String(spec.port);

        const readout = document.createElement("output");
        readout.textContent = "0";

        slider.addEventListener("input", () => {
          readout.textContent = slider.value;
          try {
            this.engine.setOutput(spec.port, Number(slider.value));
          } catch (error) {
            this.options.onError(error);
          }
        });

        row.append(label, slider, readout);
        return row;
      }),
    );
    if (!product.outputs.length) {
      this.el.outputs.textContent = "이 제품에는 출력 포트가 없습니다.";
    }
  }

  private draw(view: EngineView): void {
    const connected = view.diagnostics.product !== null;
    const product = view.diagnostics.product ?? this.engine.selectedProduct;
    this.buildOutputs(product);

    this.el.stateLine.textContent = `${view.state} — ${view.message}`;
    this.el.stateLine.dataset.state = view.state;
    this.el.hintLine.textContent = view.hint ?? "";
    this.el.hintLine.hidden = !view.hint;

    const busy = view.state === "connecting" || view.state === "disconnecting";
    this.el.product.disabled = connected || busy;
    this.el.leftSign.disabled = connected || busy;
    this.el.rightSign.disabled = connected || busy;
    this.el.requestPort.disabled = view.state !== "idle";
    this.el.requestAnyPort.disabled = view.state !== "idle";
    this.el.connectKnown.disabled = view.state !== "idle" || this.el.knownPorts.options.length === 0;
    this.el.disconnect.disabled = !connected;

    this.el.emergencyStop.disabled = view.state !== "active" && view.state !== "ready";
    this.el.activate.disabled = !view.canActivate;
    this.el.activateReason.textContent = view.activateBlockedReason ?? "";

    const snapshot = view.snapshot;
    this.el.speedReadout.textContent = snapshot
      ? `L ${snapshot.left.toFixed(1)}   R ${snapshot.right.toFixed(1)}`
      : "L —   R —";

    for (const slider of this.el.outputs.querySelectorAll<HTMLInputElement>("input[type=range]")) {
      slider.disabled = view.state !== "active";
      const port = Number(slider.dataset.port);
      const value = snapshot?.outputs[port - 1];
      if (value !== undefined && Number(slider.value) !== value) {
        slider.value = String(value);
        const readout = slider.nextElementSibling;
        if (readout) readout.textContent = String(value);
      }
    }

    this.drawSensors(view, product);
    this.drawDiagnostics(view, product);
    this.drawEvents(view);
  }

  private drawSensors(view: EngineView, product: ProductInfo): void {
    const sensors = view.snapshot?.sensors ?? null;
    const rows: Array<[string, string]> = [];
    for (let port = 1; port <= product.in_ports; port += 1) {
      const key = `IN${port}`;
      rows.push([key, sensors && key in sensors ? formatSensor(sensors[key]!) : "—"]);
    }
    for (const key of ["IR", "BAT"]) {
      rows.push([key, sensors && key in sensors ? formatSensor(sensors[key]!) : "—"]);
    }
    this.el.sensors.replaceChildren(
      ...rows.map(([key, value]) => {
        const tr = document.createElement("tr");
        const th = document.createElement("th");
        th.textContent = key;
        const td = document.createElement("td");
        td.textContent = value;
        tr.append(th, td);
        return tr;
      }),
    );
  }

  private drawDiagnostics(view: EngineView, product: ProductInfo): void {
    const d = view.diagnostics;
    const stats = view.snapshot?.stats ?? {};
    const intervals = d.intervals;
    const stopText =
      d.stopConfirmation === "sent"
        ? "정지 명령 전송 완료"
        : d.stopConfirmation === "unconfirmed"
          ? "정지 전송 확인 불가"
          : "—";
    const rows: Array<[string, string]> = [
      ["제품", `${product.label} [${product.id}]`],
      [
        "USB VID/PID",
        d.usbVendorId === null
          ? "—"
          : `0x${d.usbVendorId.toString(16)} / ${d.usbProductId === null ? "—" : `0x${d.usbProductId.toString(16)}`}`,
      ],
      ["baud", dash(d.baud)],
      ["목표 주기", dash(d.targetIntervalMs, " ms")],
      [
        "실측 주기 (중앙값/p95/최대)",
        intervals.length
          ? `${median(intervals)!.toFixed(1)} / ${percentile(intervals, 0.95)!.toFixed(1)} / ${Math.max(...intervals).toFixed(1)} ms`
          : "—",
      ],
      ["tx (성공한 write)", String(d.txWrites)],
      ["rx (센서 프레임)", String(d.rxFrames)],
      ["checksum 오류", dash(stats["bad_checksums"] ?? null)],
      ["버린 바이트", dash(stats["discarded"] ?? null)],
      [
        "마지막 센서 이후",
        d.sinceLastSensorMs === null ? "—" : `${Math.round(d.sinceLastSensorMs)} ms`,
      ],
      ["오류 수", String(d.errors)],
      ["정지 전송", stopText],
    ];
    this.el.diagnostics.replaceChildren(
      ...rows.map(([key, value]) => {
        const tr = document.createElement("tr");
        const th = document.createElement("th");
        th.textContent = key;
        const td = document.createElement("td");
        td.textContent = value;
        tr.append(th, td);
        return tr;
      }),
    );
  }

  private drawEvents(view: EngineView): void {
    if (view.events.length === this.lastEventCount) return;
    this.lastEventCount = view.events.length;
    this.el.events.replaceChildren(
      ...[...view.events].reverse().map((event) => {
        const li = document.createElement("li");
        li.dataset.level = event.level;
        li.textContent = `${(event.at / 1000).toFixed(1)}s  ${event.message}`;
        return li;
      }),
    );
  }
}
